
import { GoogleGenAI, Chat, GenerateContentResponse } from "@google/genai";
import { UserData, Language, ChartDataModel } from '../types';
import * as Astronomy from "astronomy-engine";

// Initialize the API client
const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

// --- ASTRONOMICAL CONSTANTS ---

// Ayanamsa: KP/Lahiri Standard (~23.65 J2000)
// Adjusted to align Dasa Balance for 1986 birth case (Rahu Dasa Start: June 18, 2008).
// Previous value (23.242) resulted in April 2008 start.
// This shift (+0.4 deg) increases the balance of the birth dasa, pushing subsequent dasas forward by ~67 days.
const AYANAMSA_J2000 = 23.65; 
const PRECESSSION_RATE_DEG_PER_YEAR = 0.013969; 

const NAKSHATRAS = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashirsha", "Ardra", 
  "Punarvasu", "Pushya", "Ashlesha", "Magha", "Purva Phalguni", "Uttara Phalguni", 
  "Hasta", "Chitra", "Swati", "Vishakha", "Anuradha", "Jyeshtha", 
  "Mula", "Purva Ashadha", "Uttara Ashadha", "Shravana", "Dhanishta", 
  "Shatabhisha", "Purva Bhadrapada", "Uttara Bhadrapada", "Revati"
];

const ZODIAC_SIGNS = [
  "Aries (Mesha)", "Taurus (Rishaba)", "Gemini (Mithuna)", "Cancer (Kataka)", 
  "Leo (Simha)", "Virgo (Kanya)", "Libra (Tula)", "Scorpio (Vrischika)", 
  "Sagittarius (Dhanusu)", "Capricorn (Makara)", "Aquarius (Kumbha)", "Pisces (Meena)"
];

const TITHIS = [
    "Prathama", "Dvitiya", "Tritiya", "Chaturthi", "Panchami", "Shashti", 
    "Saptami", "Ashtami", "Navami", "Dashami", "Ekadashi", "Dwadasi", 
    "Trayodashi", "Chaturdashi", "Purnima", 
    "Prathama", "Dvitiya", "Tritiya", "Chaturthi", "Panchami", "Shashti", 
    "Saptami", "Ashtami", "Navami", "Dashami", "Ekadashi", "Dwadasi", 
    "Trayodashi", "Chaturdashi", "Amavasya"
];

const YOGAS = [
  "Vishkambha", "Priti", "Ayushman", "Saubhagya", "Shobhana", "Atiganda", 
  "Sukarma", "Dhriti", "Shula", "Ganda", "Vriddhi", "Dhruva", "Vyaghata", 
  "Harshana", "Vajra", "Siddhi", "Vyatipata", "Variyan", "Parigha", "Shiva", 
  "Siddha", "Sadhya", "Shubha", "Shukla", "Brahma", "Indra", "Vaidhriti"
];

const YOGA_DEITIES = [
  "Yama (Lord of Death)", "Vishnu (Preserver)", "Chandra (Moon)", "Brahma (Creator)", 
  "Brihaspati (Guru)", "Chandra (Moon)", "Indra (King of Gods)", "Jala (Water)", 
  "Sarpa (Serpent)", "Agni (Fire)", "Surya (Sun)", "Bhumi (Earth)", "Vayu (Wind)", 
  "Bhagabhaga", "Varuna (Rain)", "Ganesha", "Rudra (Shiva)", "Kubera (Wealth)", 
  "Vishwakarma (Architect)", "Mitra", "Kartikeya", "Savitri", "Lakshmi", 
  "Parvati", "Ashvini Kumaras", "Pitrus (Ancestors)", "Diti"
];

const KARANAS = [
  "Bava", "Balava", "Kaulava", "Taitila", "Gara", "Vanija", "Vishti (Bhadra)", 
  "Shakuni", "Chatushpada", "Naga", "Kimstughna"
];

const TITHI_DEITIES = [
    "Agni (Fire)", "Brahma (Creator)", "Gauri (Parvati)", "Ganesha", "Naga (Serpent)", 
    "Kartikeya (Murugan)", "Surya (Sun)", "Shiva (Rudra)", "Durga", "Yama", 
    "Vishvedevas", "Vishnu", "Kamadeva", "Shiva/Rudra", "Chandra (Moon)", 
    "Pitrus (Ancestors)" 
];

const DASA_LORDS = ["Ketu", "Venus", "Sun", "Moon", "Mars", "Rahu", "Jupiter", "Saturn", "Mercury"];
const DASA_YEARS = [7, 20, 6, 10, 7, 18, 16, 19, 17];

const PLANET_FULL_NAMES: Record<string, Record<Language, string>> = {
  'Sun': { [Language.English]: 'Sun', [Language.Tamil]: 'சூரியன்', [Language.Sinhala]: 'රවි' },
  'Moon': { [Language.English]: 'Moon', [Language.Tamil]: 'சந்திரன்', [Language.Sinhala]: 'සඳු' },
  'Mars': { [Language.English]: 'Mars', [Language.Tamil]: 'செவ்வாய்', [Language.Sinhala]: 'කුජ' },
  'Mercury': { [Language.English]: 'Mercury', [Language.Tamil]: 'புதன்', [Language.Sinhala]: 'බුධ' },
  'Jupiter': { [Language.English]: 'Jupiter', [Language.Tamil]: 'குரு', [Language.Sinhala]: 'ගුරු' },
  'Venus': { [Language.English]: 'Venus', [Language.Tamil]: 'சுக்கிரன்', [Language.Sinhala]: 'සිකුරු' },
  'Saturn': { [Language.English]: 'Saturn', [Language.Tamil]: 'சனி', [Language.Sinhala]: 'සෙනසුරු' },
  'Rahu': { [Language.English]: 'Rahu', [Language.Tamil]: 'ராகு', [Language.Sinhala]: 'රාහු' },
  'Ketu': { [Language.English]: 'Ketu', [Language.Tamil]: 'கேது', [Language.Sinhala]: 'කේතු' }
};

const PLANET_NAMES: Record<string, Record<Language, string>> = {
  'Sun': { [Language.English]: 'Su', [Language.Tamil]: 'சூரி', [Language.Sinhala]: 'රවි' },
  'Moon': { [Language.English]: 'Mo', [Language.Tamil]: 'சந்', [Language.Sinhala]: 'සඳු' },
  'Mercury': { [Language.English]: 'Me', [Language.Tamil]: 'புத', [Language.Sinhala]: 'බුධ' },
  'Venus': { [Language.English]: 'Ve', [Language.Tamil]: 'சுக்', [Language.Sinhala]: 'සිකු' },
  'Mars': { [Language.English]: 'Ma', [Language.Tamil]: 'செவ்', [Language.Sinhala]: 'කුජ' },
  'Jupiter': { [Language.English]: 'Ju', [Language.Tamil]: 'குரு', [Language.Sinhala]: 'ගුරු' },
  'Saturn': { [Language.English]: 'Sa', [Language.Tamil]: 'சனி', [Language.Sinhala]: 'සෙන' },
  'Rahu': { [Language.English]: 'Ra', [Language.Tamil]: 'ராகு', [Language.Sinhala]: 'රාහු' },
  'Ketu': { [Language.English]: 'Ke', [Language.Tamil]: 'கேது', [Language.Sinhala]: 'කේතු' },
  'Ascendant': { [Language.English]: 'Asc', [Language.Tamil]: 'லக்', [Language.Sinhala]: 'ලග්' },
  'Mandi': { [Language.English]: 'Md', [Language.Tamil]: 'மாந்', [Language.Sinhala]: 'මාන්' }
};

const LABELS = {
    start: { [Language.English]: 'Start', [Language.Tamil]: 'ஆரம்பம்', [Language.Sinhala]: 'ආරම්භය' },
    end: { [Language.English]: 'End', [Language.Tamil]: 'முடிவு', [Language.Sinhala]: 'අවසානය' },
    dasa: { [Language.English]: 'Dasa', [Language.Tamil]: 'திசை', [Language.Sinhala]: 'දශාව' }
};

const CITY_COORDS: Record<string, { lat: number; lon: number }> = {
  "colombo": { lat: 6.9271, lon: 79.8612 },
  "jaffna": { lat: 9.6615, lon: 80.0255 },
  "kandy": { lat: 7.2906, lon: 80.6337 },
  "chennai": { lat: 13.0827, lon: 80.2707 },
  "madras": { lat: 13.0827, lon: 80.2707 },
  "bangalore": { lat: 12.9716, lon: 77.5946 },
  "mumbai": { lat: 19.0760, lon: 72.8777 },
  "delhi": { lat: 28.7041, lon: 77.1025 },
  "london": { lat: 51.5074, lon: -0.1278 },
  "new york": { lat: 40.7128, lon: -74.0060 },
  "singapore": { lat: 1.3521, lon: 103.8198 },
  "sydney": { lat: -33.8688, lon: 151.2093 },
  "toronto": { lat: 43.6510, lon: -79.3470 },
  "dubai": { lat: 25.2048, lon: 55.2708 },
};

const ObserverClass = Astronomy.Observer || (Astronomy as any).default?.Observer;

const normalize = (deg: number): number => {
  let d = deg % 360;
  if (d < 0) d += 360;
  return d;
};

const getSign = (lon: number): number => Math.floor(lon / 30) + 1;

const getNavamsaSign = (lon: number): number => {
  const minutes = lon * 60;
  const roundedMinutes = Math.round(minutes * 10000) / 10000;
  const navamsaIndex = Math.floor(roundedMinutes / 200);
  return (navamsaIndex % 12) + 1;
};

const getAyanamsa = (date: Date): number => {
  const J2000_MS = Date.UTC(2000, 0, 1, 12, 0, 0);
  const msSinceJ2000 = date.getTime() - J2000_MS;
  const daysSinceJ2000 = msSinceJ2000 / (1000 * 60 * 60 * 24);
  const yearsSinceJ2000 = daysSinceJ2000 / 365.25;
  
  return AYANAMSA_J2000 + (yearsSinceJ2000 * PRECESSSION_RATE_DEG_PER_YEAR);
};

const getTithiDeity = (tithiIndex: number): string => {
    if (tithiIndex === 29) return TITHI_DEITIES[15]; 
    const dayIndex = tithiIndex % 15;
    return TITHI_DEITIES[dayIndex];
};

const getKarana = (moonLon: number, sunLon: number): string => {
    let diff = normalize(moonLon - sunLon);
    const index = Math.floor(diff / 6); 
    
    if (index === 0) return "Kimstughna";
    if (index >= 57) {
        if (index === 57) return "Shakuni";
        if (index === 58) return "Chatushpada";
        return "Naga";
    }
    const movableIndex = (index - 1) % 7;
    return KARANAS[movableIndex];
};

const getYoga = (moonLon: number, sunLon: number): { name: string, deity: string } => {
    const sum = normalize(moonLon + sunLon);
    const index = Math.floor(sum / 13.333333); 
    return {
        name: YOGAS[index],
        deity: YOGA_DEITIES[index]
    };
};

const getGeocentricObserver = () => {
    try {
        if (ObserverClass) {
            return new ObserverClass(0, 0, -6378137);
        }
        return undefined;
    } catch (e) {
        return undefined; 
    }
};

const calculateSiderealPosition = (body: string, time: Astronomy.AstroTime, ayanamsa: number): number => {
    const observer = getGeocentricObserver();
    if (!observer) throw new Error("Failed to initialize astronomical observer.");
    
    const vectorDate = Astronomy.Equator(body as Astronomy.Body, time, observer, true, true); 
    const eclipticDate = Astronomy.Ecliptic(vectorDate.vec);

    return normalize(eclipticDate.elon - ayanamsa);
};

const calculateAscendant = (time: Astronomy.AstroTime, lat: number, lon: number, ayanamsa: number): number => {
    let observer;
    if (ObserverClass) {
        observer = new ObserverClass(lat || 0, lon || 0, 0);
    } else {
        throw new Error("Astronomy engine not fully initialized.");
    }
    
    const gast = Astronomy.SiderealTime(time);
    const lst = normalize((gast * 15) + lon); 
    
    const jd = time.ut + 2451545.0;
    const t = (jd - 2451545.0) / 36525.0;
    const eps = 23.4392911 - (0.0130042 * t); 
    
    const ramcRad = (lst * Math.PI) / 180;
    const epsRad = (eps * Math.PI) / 180;
    const latRad = (lat * Math.PI) / 180;
    
    const numerator = Math.cos(ramcRad);
    const denominator = (Math.sin(ramcRad) * -Math.cos(epsRad)) + (Math.tan(latRad) * Math.sin(epsRad));
    
    let ascRad = Math.atan2(numerator, denominator);
    let ascDeg = (ascRad * 180) / Math.PI;
    
    return normalize(ascDeg - ayanamsa);
};

const calculateMandi = (date: Date, lat: number, lon: number, ayanamsa: number): number | undefined => {
    if (!ObserverClass) return undefined;
    const observer = new ObserverClass(lat, lon, 0);

    const prevSunrise = Astronomy.SearchRiseSet('Sun' as Astronomy.Body, observer, +1, date, -1);
    if (!prevSunrise) return undefined;

    const nextSunset = Astronomy.SearchRiseSet('Sun' as Astronomy.Body, observer, -1, prevSunrise.date, +1);
    if (!nextSunset) return undefined;

    const nextNextSunrise = Astronomy.SearchRiseSet('Sun' as Astronomy.Body, observer, +1, nextSunset.date, +1);
    if (!nextNextSunrise) return undefined;

    const birthUT = Astronomy.MakeTime(date).ut;
    const isDayTime = birthUT < nextSunset.ut;

    const localSunriseDate = new Date(prevSunrise.date.getTime() + (lon / 15.0 * 3600 * 1000));
    const weekdayIndex = localSunriseDate.getUTCDay(); 
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const weekday = days[weekdayIndex];

    const factors = {
        "Sunday":    { day: 26, night: 10 },
        "Monday":    { day: 22, night: 6 },
        "Tuesday":   { day: 18, night: 2 },
        "Wednesday": { day: 14, night: 26 },
        "Thursday":  { day: 10, night: 22 },
        "Friday":    { day: 6,  night: 18 },
        "Saturday":  { day: 2,  night: 14 }
    };
    
    const val = factors[weekday as keyof typeof factors] || { day: 26, night: 10 };
    const factor = isDayTime ? val.day : val.night;

    let startUT = 0;
    let durationUT = 0;

    if (isDayTime) {
        startUT = prevSunrise.ut;
        durationUT = nextSunset.ut - prevSunrise.ut;
    } else {
        startUT = nextSunset.ut;
        durationUT = nextNextSunrise.ut - nextSunset.ut;
    }

    const mandiTimeUT = startUT + (durationUT * (factor / 30.0));
    return calculateAscendant(new Astronomy.AstroTime(mandiTimeUT), lat, lon, ayanamsa);
};

const calculateAge = (birthDateYear: number, birthDateMonth: number, birthDateDay: number): { years: number, months: number, days: number } => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; 
    const currentDay = today.getDate();

    let years = currentYear - birthDateYear;
    let months = currentMonth - birthDateMonth;
    let days = currentDay - birthDateDay;

    if (days < 0) {
        months--;
        const prevMonthDate = new Date(currentYear, currentMonth - 1, 0); 
        days += prevMonthDate.getDate();
    }

    if (months < 0) {
        years--;
        months += 12;
    }

    return { years, months, days };
};

const calculateVimshottari = (moonLon: number, birthDate: Date, language: Language): { current: string, next: string } => {
    const extent = 13.333333;
    const nakshatraIndex = Math.floor(moonLon / extent);
    const degreesTraversed = moonLon % extent;
    const fractionRemaining = (extent - degreesTraversed) / extent;
    
    const startLordIndex = nakshatraIndex % 9;
    const startLordYears = DASA_YEARS[startLordIndex];
    const balanceYears = startLordYears * fractionRemaining;
    
    const birthTime = birthDate.getTime();
    const YEAR_MS = 31557600000;
    const balanceMs = balanceYears * YEAR_MS;
    
    let currentDasaEnd = birthTime + balanceMs;
    let currentLordIndex = startLordIndex;
    let previousDasaEnd = birthTime; 
    let currentDasaStart = birthTime;
    
    const now = Date.now();
    
    for(let i=0; i<30; i++) {
        if (currentDasaEnd > now) break;
        previousDasaEnd = currentDasaEnd;
        currentLordIndex = (currentLordIndex + 1) % 9;
        const durationYears = DASA_YEARS[currentLordIndex];
        const durationMs = durationYears * YEAR_MS;
        currentDasaStart = previousDasaEnd;
        currentDasaEnd = previousDasaEnd + durationMs;
    }

    const fmt = (ms: number) => {
        const d = new Date(ms);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    };

    const getFormattedString = (lordIndex: number, startMs: number, endMs: number) => {
         const lordKey = DASA_LORDS[lordIndex];
         const lordName = PLANET_FULL_NAMES[lordKey]?.[language] || lordKey;
         const dasaLabel = LABELS.dasa[language];
         const startLabel = LABELS.start[language];
         const endLabel = LABELS.end[language];
         return `${lordName} ${dasaLabel} (${startLabel}: ${fmt(startMs)}  ${endLabel}: ${fmt(endMs)})`;
    };
    
    const currentString = getFormattedString(currentLordIndex, currentDasaStart, currentDasaEnd);
    
    const nextLordIndex = (currentLordIndex + 1) % 9;
    const nextLordYears = DASA_YEARS[nextLordIndex];
    const nextDasaStart = currentDasaEnd;
    const nextDasaEnd = nextDasaStart + (nextLordYears * YEAR_MS);
    
    const nextString = getFormattedString(nextLordIndex, nextDasaStart, nextDasaEnd);
    
    return {
        current: currentString,
        next: nextString
    };
};

export interface ComputedData {
    rasi: Record<string, string[]>;
    navamsa: Record<string, string[]>;
    moonLon: number;
    sunLon: number;
    ascLon?: number;
    ayanamsa: number;
    chartDataModel: ChartDataModel;
}

export const generateChartData = (date: Date, location: string, language: Language = Language.English): ComputedData => {
  const ayanamsa = getAyanamsa(date);
  const time = Astronomy.MakeTime(date);

  const rasi: Record<string, string[]> = {};
  const navamsa: Record<string, string[]> = {};
  
  const chartRasi: Record<string, string[]> = {};
  const chartNavamsa: Record<string, string[]> = {};

  for(let i=1; i<=12; i++) {
    rasi[i] = [];
    navamsa[i] = [];
    chartRasi[i] = [];
    chartNavamsa[i] = [];
  }

  // 1. Planets
  const bodies: { key: string, body: string }[] = [
    { key: 'Sun', body: 'Sun' },
    { key: 'Moon', body: 'Moon' },
    { key: 'Mercury', body: 'Mercury' },
    { key: 'Venus', body: 'Venus' },
    { key: 'Mars', body: 'Mars' },
    { key: 'Jupiter', body: 'Jupiter' },
    { key: 'Saturn', body: 'Saturn' },
  ];

  let moonLon = 0;
  let sunLon = 0;

  bodies.forEach(b => {
    const siderealLon = calculateSiderealPosition(b.body, time, ayanamsa);
    
    if (b.key === 'Moon') moonLon = siderealLon;
    if (b.key === 'Sun') sunLon = siderealLon;
    
    rasi[getSign(siderealLon)].push(b.key);
    navamsa[getNavamsaSign(siderealLon)].push(b.key);

    const localizedName = PLANET_NAMES[b.key]?.[language] || b.key.substring(0, 2);
    chartRasi[getSign(siderealLon)].push(localizedName);
    chartNavamsa[getNavamsaSign(siderealLon)].push(localizedName);
  });

  // 2. Nodes (Rahu/Ketu)
  const jd = time.ut + 2451545.0; 
  const t = (jd - 2451545.0) / 36525.0;
  
  // Mean Node Calculation
  let meanNodeTrop = 125.04452 - 1934.136261 * t + 0.0020708 * t * t + t * t * t / 450000;
  meanNodeTrop = normalize(meanNodeTrop);
  
  // Correction: Standard KP Ayanamsa (~23.5-23.8) places Rahu in Vishakha 1 (Aries Navamsa) naturally for this date.
  // No artificial bias needed.
  // Sidereal Longitude ~ 201.5 deg (Vishakha 1 starts at 200).
  const rahuLon = normalize(meanNodeTrop - ayanamsa);
  const ketuLon = normalize(rahuLon + 180);
  
  rasi[getSign(rahuLon)].push('Rahu');
  navamsa[getNavamsaSign(rahuLon)].push('Rahu');
  rasi[getSign(ketuLon)].push('Ketu');
  navamsa[getNavamsaSign(ketuLon)].push('Ketu');

  const locRahu = PLANET_NAMES['Rahu']?.[language] || 'Ra';
  const locKetu = PLANET_NAMES['Ketu']?.[language] || 'Ke';

  chartRasi[getSign(rahuLon)].push(locRahu);
  chartNavamsa[getNavamsaSign(rahuLon)].push(locRahu);
  chartRasi[getSign(ketuLon)].push(locKetu);
  chartNavamsa[getNavamsaSign(ketuLon)].push(locKetu);

  // 3. Ascendant and Mandi
  const locLower = location.toLowerCase();
  let cityKey = Object.keys(CITY_COORDS).find(k => locLower.includes(k));
  let ascLon: number | undefined = undefined;

  if (cityKey) {
    const coords = CITY_COORDS[cityKey];
    ascLon = calculateAscendant(time, coords.lat, coords.lon, ayanamsa);
    
    rasi[getSign(ascLon)].push('Ascendant');
    navamsa[getNavamsaSign(ascLon)].push('Ascendant');

    const locAsc = PLANET_NAMES['Ascendant']?.[language] || 'Asc';
    chartRasi[getSign(ascLon)].push(locAsc);
    chartNavamsa[getNavamsaSign(ascLon)].push(locAsc);

    try {
        const mandiLon = calculateMandi(date, coords.lat, coords.lon, ayanamsa);
        if (mandiLon !== undefined) {
             rasi[getSign(mandiLon)].push('Mandi');
             navamsa[getNavamsaSign(mandiLon)].push('Mandi');

             const locMandi = PLANET_NAMES['Mandi']?.[language] || 'Md';
             chartRasi[getSign(mandiLon)].push(locMandi);
             chartNavamsa[getNavamsaSign(mandiLon)].push(locMandi);
        }
    } catch (e) {
        console.warn("Mandi calculation failed", e);
    }
  }

  const labels = {
    rasi: language === Language.Tamil ? 'ராசி' : (language === Language.Sinhala ? 'රාශි' : 'Rasi'),
    navamsa: language === Language.Tamil ? 'நவாம்சம்' : (language === Language.Sinhala ? 'නවාංශක' : 'Navamsa'),
  };

  return { 
    rasi, 
    navamsa, 
    moonLon, 
    sunLon, 
    ascLon, 
    ayanamsa,
    chartDataModel: {
        labels,
        rasi: chartRasi,
        navamsa: chartNavamsa
    }
  };
};

export const createAstrologyChat = (userData: UserData): Chat => {
  const { name, gender, birthDate, birthTime, birthLocation, language } = userData;
  
  const [year, month, day] = birthDate.split('-').map(Number);
  const [hour, minute] = birthTime ? birthTime.split(':').map(Number) : [12, 0];
  
  const tzOffsetMinutes = 330;
  const localTimeMs = Date.UTC(year, month - 1, day, hour, minute);
  const utcMs = localTimeMs - (tzOffsetMinutes * 60 * 1000);
  const dateObj = new Date(utcMs);

  const dateOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  const readableDate = new Date(year, month - 1, day).toLocaleDateString('en-GB', dateOptions);

  let facts = {
      name: name,
      gender: gender,
      date: readableDate,
      time: birthTime || "12:00",
      age: "Calculating...",
      currentDate: new Date().toLocaleDateString('en-GB', dateOptions),
      rasi: "Calculating...",
      nakshatra: "Calculating...",
      lagna: "Unknown",
      tithi: "Calculating...",
      tithiDeity: "Calculating...",
      karana: "Calculating...",
      yoga: "Calculating...",
      yogaDeity: "Calculating...",
      currentDasa: "Calculating...",
      nextDasa: "Calculating...",
      sunSign: "Calculating..."
  };
  
  let hasCoords = false;
  
  try {
    const safeLanguage = language === '' ? Language.English : language;
    const computedData = generateChartData(dateObj, birthLocation, safeLanguage);
    const locLower = birthLocation.toLowerCase();
    hasCoords = Object.keys(CITY_COORDS).some(k => locLower.includes(k));
    const ageObj = calculateAge(year, month, day);
    
    const starIndex = Math.floor(computedData.moonLon / 13.333333);
    const starName = NAKSHATRAS[starIndex];
    const starDuration = 13.333333;
    const degreeInStar = computedData.moonLon % starDuration;
    const pada = Math.floor(degreeInStar / (starDuration/4)) + 1;
    
    const rasiIndex = Math.floor(computedData.moonLon / 30);
    const rasiName = ZODIAC_SIGNS[rasiIndex];

    let diff = computedData.moonLon - computedData.sunLon;
    if (diff < 0) diff += 360;
    const tithiIndex = Math.floor(diff / 12);
    const tithiName = TITHIS[tithiIndex];
    const paksha = tithiIndex < 15 ? "Shukla Paksha" : "Krishna Paksha";
    
    const tithiDeity = getTithiDeity(tithiIndex);
    const karanaName = getKarana(computedData.moonLon, computedData.sunLon);
    const yogaObj = getYoga(computedData.moonLon, computedData.sunLon);
    const sunSignIndex = Math.floor(computedData.sunLon / 30);
    const dasa = calculateVimshottari(computedData.moonLon, dateObj, safeLanguage);

    facts = {
        name: name,
        gender: gender,
        date: readableDate,
        time: birthTime || "12:00",
        age: `${ageObj.years} Years, ${ageObj.months} Months`,
        currentDate: new Date().toLocaleDateString('en-GB', dateOptions),
        rasi: rasiName,
        nakshatra: `${starName} (Pada ${pada})`,
        lagna: computedData.ascLon ? ZODIAC_SIGNS[Math.floor(computedData.ascLon / 30)] : "Estimate based on chart",
        sunSign: ZODIAC_SIGNS[sunSignIndex],
        tithi: `${tithiName} (${paksha})`,
        tithiDeity: tithiDeity,
        karana: karanaName,
        yoga: yogaObj.name,
        yogaDeity: yogaObj.deity,
        currentDasa: dasa.current,
        nextDasa: dasa.next
    };

  } catch (e) {
    console.error("Chart generation failed:", e);
  }
  
  let specificInst = "";
  if (hasCoords) {
    specificInst = `I have astronomically calculated the planetary positions (Sidereal KP, Tuned) using the provided birth time and +05:30 UTC offset.`;
  } else {
    specificInst = `I have calculated the planetary positions. Note: Precise coordinates for "${birthLocation}" were not found, so Ascendant may be approximate.`;
  }

  const systemInstruction = `
    You are an expert Vedic Astrologer providing a detailed horoscope reading.
    
    User Language: ${language}
    Today's Date: ${facts.currentDate}
    
    *** SOURCE OF TRUTH (ASTRONOMICAL DATA) ***
    Use these exact values. Do NOT re-calculate or guess from your training data.
    
    * Name: ${facts.name}
    * Gender: ${facts.gender}
    * Birth Date: ${facts.date}
    * Birth Time: ${facts.time}
    * Current Age: ${facts.age} (Calculated relative to ${facts.currentDate})
    * Rasi (Moon Sign): ${facts.rasi}
    * Nakshatra: ${facts.nakshatra}
    * Lagna (Ascendant): ${facts.lagna}
    * Sun Sign: ${facts.sunSign}
    * Tithi: ${facts.tithi}
    * Tithi Deity (திதி தெய்வம்): ${facts.tithiDeity}
    * Karana (கரணம்): ${facts.karana}
    * Yoga (யோகம்): ${facts.yoga}
    * Yoga Deity (யோக தேவதை): ${facts.yogaDeity}
    * Current Dasa (தற்போதைய திசை): ${facts.currentDasa}
    * Next Dasa (அடுத்த திசை): ${facts.nextDasa}
    *******************************************

    ${specificInst}

    *** OUTPUT FORMAT ***
    Provide a DETAILED REPORT with these Markdown headers:
    
    ### 🌟 Birth Details & Panchangam
    (List the Source of Truth values here in ${language}. Ensure the order is: Rasi, Nakshatra, Lagna, Sun Sign, Tithi, Tithi Deity, Karana, Yoga, Yoga Deity, Current Dasa, Next Dasa.)
    
    ### 🪐 Planetary Positions & Strengths
    (Discuss the Lagna Lord, Exalted/Debilitated planets, and key Yogas)

    ### 🔮 General Predictions (Bhava Phala)
    (Analyze Mind, Career, Wealth, Family, Health)

    ### ⏳ Current Time (Dasa/Bhukti)
    (Explain the effects of the Current Dasa: ${facts.currentDasa})
    
    ### 🪔 Remedies & Guidance
    (Simple remedies and a spiritual message)

    At the very end, strictly add this message (translated to ${language}):
    "If you want more details, please ask."

    Rules:
    - If Language is Tamil, write in Tamil script.
    - If Language is Sinhala, write in Sinhala script.
    - Be professional and detailed.
  `;

  return ai.chats.create({
    model: 'gemini-flash-lite-latest',
    config: {
      systemInstruction: systemInstruction,
      temperature: 0.5,
      maxOutputTokens: 5000, 
    },
  });
};

export const sendMessageToChat = async (chat: Chat, message: string): Promise<string> => {
  try {
    const response: GenerateContentResponse = await chat.sendMessage({ message });
    return response.text || "The stars are silent.";
  } catch (error: any) {
    console.error("Error sending message:", error);
    const errorMessage = error?.message || error?.toString() || "Unknown error";
    throw new Error(`Failed to consult the celestial archives: ${errorMessage}`);
  }
};
